import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";

import type { BestellStatus, BestellungIntern, HerstellerBestellung } from "@repo/shared";

import { api, errorMessage } from "../lib/api";
import { STATUS, datum, fortschritt, margeProzent, statusLabel, zeitpunkt } from "../lib/bestellung";
import { euro } from "../lib/regeln";

export function BestellungDetailPage() {
  const { id = "" } = useParams();
  const [bestellung, setBestellung] = useState<BestellungIntern | null>(null);
  const [laedt, setLaedt] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);
  const alsGelesenMarkiert = useRef(false);

  const uebernehmen = useCallback(async (res: Response, fallback: string) => {
    if (!res.ok) {
      setFehler(await errorMessage(res, fallback));
      return false;
    }
    const daten = (await res.json()) as { bestellung?: BestellungIntern };
    if (daten.bestellung) setBestellung(daten.bestellung);
    setFehler(null);
    return true;
  }, []);

  useEffect(() => {
    let aktiv = true;
    void (async () => {
      const res = await api.api.admin.bestellungen[":id"].$get({ param: { id } });
      if (!aktiv) return;
      await uebernehmen(res, "Bestellung nicht ladbar");
      setLaedt(false);
    })();
    return () => {
      aktiv = false;
    };
  }, [id, uebernehmen]);

  // Öffnen heißt lesen – einmal je Aufruf, wie bei E-Mails.
  useEffect(() => {
    if (!bestellung || bestellung.gelesenAm || alsGelesenMarkiert.current) return;
    alsGelesenMarkiert.current = true;
    void api.api.admin.bestellungen[":id"].gelesen
      .$put({ param: { id: bestellung.id }, json: { gelesen: true } })
      .then((res) => uebernehmen(res, "Konnte nicht als gelesen markiert werden"));
  }, [bestellung, uebernehmen]);

  if (laedt) return <p className="muted">Lade Bestellung …</p>;
  if (!bestellung) return <p className="error">{fehler ?? "Bestellung nicht gefunden"}</p>;

  const b = bestellung;
  const f = fortschritt(b);
  const a = b.lieferadresse;

  async function setzeStatus(status: BestellStatus) {
    await uebernehmen(
      await api.api.admin.bestellungen[":id"].status.$put({ param: { id: b.id }, json: { status } }),
      "Status konnte nicht gesetzt werden",
    );
  }

  async function alsUngelesen() {
    await uebernehmen(
      await api.api.admin.bestellungen[":id"].gelesen.$put({ param: { id: b.id }, json: { gelesen: false } }),
      "Konnte nicht markiert werden",
    );
  }

  return (
    <section>
      <p className="breadcrumb">
        <Link to="/bestellungen">Bestellungen</Link> / {b.nummer}
      </p>
      <div className="page-head">
        <h1>
          {b.nummer} <span className={`badge badge--status-${b.status}`}>{statusLabel(b.status)}</span>
        </h1>
        <div className="actions">
          <button type="button" onClick={() => void alsUngelesen()}>
            Als ungelesen markieren
          </button>
          <select
            aria-label="Status"
            value={b.status}
            onChange={(e) => void setzeStatus(e.target.value as BestellStatus)}
          >
            {STATUS.map((s) => (
              <option key={s.wert} value={s.wert}>
                {s.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      <p className="muted small">
        Eingegangen am {zeitpunkt(b.createdAt)} · {b.mandantName}
        {b.externeReferenz && (
          <>
            {" "}
            · Referenz <code>{b.externeReferenz}</code>
          </>
        )}
      </p>

      {fehler && <p className="error">{fehler}</p>}

      <div className="kennzahlen">
        <div className="kennzahl">
          <span>VK netto</span>
          <strong>{euro(b.summeNettoCents)}</strong>
          <small className="muted">brutto {euro(b.summeBruttoCents)}</small>
        </div>
        <div className="kennzahl">
          <span>EK netto</span>
          <strong>{euro(b.summeEkNettoCents)}</strong>
        </div>
        <div className="kennzahl kennzahl--betont">
          <span>Gewinn OrderCity</span>
          <strong>{euro(b.rohertragCents)}</strong>
          <small className="muted">Marge {margeProzent(b.rohertragCents, b.summeNettoCents)}</small>
        </div>
        <div className="kennzahl">
          <span>Voraussichtliche Lieferung</span>
          <strong>{datum(b.voraussichtlicheLieferungAm)}</strong>
          <small className="muted">
            {f.bestellt}/{f.gesamt} Hersteller bestellt
          </small>
        </div>
      </div>

      <div className="detail-raster">
        <div>
          <h3>Lieferadresse</h3>
          <p>
            {a.name && (
              <>
                <strong>{a.name}</strong>
                <br />
              </>
            )}
            {a.strasse}
            <br />
            {a.zusatz && (
              <>
                {a.zusatz}
                <br />
              </>
            )}
            {a.plz} {a.ort}
            <br />
            {a.land}
          </p>
        </div>
        <div>
          {b.notiz && (
            <>
              <h3>Notiz des Mandanten</h3>
              <p>{b.notiz}</p>
            </>
          )}
          {b.bestelltAm && (
            <>
              <h3>Komplett bestellt</h3>
              <p>{zeitpunkt(b.bestelltAm)}</p>
            </>
          )}
        </div>
      </div>

      <h2>Produkte nach Hersteller</h2>
      {b.hersteller.map((h) => (
        <HerstellerBlock
          key={h.schluessel}
          bestellung={b}
          hersteller={h}
          onAktualisiert={setBestellung}
          onFehler={setFehler}
        />
      ))}
    </section>
  );
}

function HerstellerBlock({
  bestellung,
  hersteller: h,
  onAktualisiert,
  onFehler,
}: {
  bestellung: BestellungIntern;
  hersteller: HerstellerBestellung;
  onAktualisiert: (b: BestellungIntern) => void;
  onFehler: (meldung: string | null) => void;
}) {
  const [tage, setTage] = useState(h.lieferzeitTage?.toString() ?? "");
  const [arbeitet, setArbeitet] = useState(false);
  const positionen = bestellung.positionen.filter((p) => h.positionIds.includes(p.id));
  const storniert = bestellung.status === "storniert";

  useEffect(() => setTage(h.lieferzeitTage?.toString() ?? ""), [h.lieferzeitTage]);

  async function speichern(bestellt: boolean) {
    const zahl = tage.trim() === "" ? null : Number(tage);
    if (bestellt && (zahl == null || !Number.isInteger(zahl) || zahl < 0)) {
      onFehler(`Bitte für ${h.name} die ungefähre Lieferzeit bis zum Lager in Tagen angeben.`);
      return;
    }
    setArbeitet(true);
    try {
      const res = await api.api.admin.bestellungen[":id"].hersteller[":schluessel"].$put({
        param: { id: bestellung.id, schluessel: h.schluessel },
        json: { bestellt, lieferzeitTage: zahl },
      });
      if (!res.ok) {
        onFehler(await errorMessage(res, "Konnte nicht gespeichert werden"));
        return;
      }
      const daten = await res.json();
      if ("bestellung" in daten) onAktualisiert(daten.bestellung);
      onFehler(null);
    } finally {
      setArbeitet(false);
    }
  }

  async function pdfHerunterladen() {
    setArbeitet(true);
    try {
      const res = await api.api.admin.bestellungen[":id"].hersteller[":schluessel"].pdf.$get({
        param: { id: bestellung.id, schluessel: h.schluessel },
      });
      if (!res.ok) {
        onFehler(await errorMessage(res, "PDF konnte nicht erstellt werden"));
        return;
      }
      const url = URL.createObjectURL(await res.blob());
      const link = document.createElement("a");
      link.href = url;
      link.download = `${bestellung.nummer}-${h.schluessel}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } finally {
      setArbeitet(false);
    }
  }

  return (
    <article className={h.bestelltAm ? "hersteller-block hersteller-block--bestellt" : "hersteller-block"}>
      <header className="hersteller-block__kopf">
        <div>
          <h3>{h.name}</h3>
          <p className="muted small">
            {positionen.length} Position{positionen.length === 1 ? "" : "en"} · EK {euro(h.summeEkNettoCents)} · VK{" "}
            {euro(h.summeVkNettoCents)} · Gewinn <strong>{euro(h.gewinnCents)}</strong>
          </p>
        </div>
        {h.bestelltAm ? (
          <span className="badge badge--active">
            Bestellt {datum(h.bestelltAm)} · Ankunft ca. {datum(h.ankunftAm)}
          </span>
        ) : (
          <span className="badge badge--suspended">Noch nicht bestellt</span>
        )}
      </header>

      <table className="table table--kompakt-positionen">
        <thead>
          <tr>
            <th>Pos.</th>
            <th>Produkt</th>
            <th className="num">Menge</th>
            <th className="num">EK/Stk</th>
            <th className="num">VK/Stk netto</th>
            <th className="num">Gewinn</th>
          </tr>
        </thead>
        <tbody>
          {positionen.map((p) => (
            <tr key={p.id}>
              <td>{p.positionsnummer}</td>
              <td>
                {p.bezeichnung}
                {p.kommission && <div className="muted small">Kommission: {p.kommission}</div>}
                <div className="muted small werte">
                  {Object.entries(p.werte)
                    .map(([k, v]) => `${k}: ${String(v)}`)
                    .join(" · ")}
                </div>
              </td>
              <td className="num">{p.menge}</td>
              <td className="num">{euro(p.preis.ekNettoCents)}</td>
              <td className="num">{euro(p.preis.vkNettoCents)}</td>
              <td className="num">{euro((p.preis.vkNettoCents - p.preis.ekNettoCents) * p.menge)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <footer className="hersteller-block__aktionen">
        <button type="button" disabled={arbeitet} onClick={() => void pdfHerunterladen()}>
          Bestell-PDF erstellen
        </button>
        <label className="field field--inline">
          Lieferzeit bis Lager
          <input
            className="input--schmal"
            type="number"
            min={0}
            max={365}
            inputMode="numeric"
            value={tage}
            disabled={storniert}
            onChange={(e) => setTage(e.target.value)}
          />
          Tage
        </label>
        {h.bestelltAm ? (
          <>
            <button
              type="button"
              disabled={arbeitet || storniert || tage === (h.lieferzeitTage?.toString() ?? "")}
              onClick={() => void speichern(true)}
            >
              Lieferzeit ändern
            </button>
            <button type="button" className="danger" disabled={arbeitet || storniert} onClick={() => void speichern(false)}>
              Zurücknehmen
            </button>
          </>
        ) : (
          <button type="button" className="primary" disabled={arbeitet || storniert} onClick={() => void speichern(true)}>
            Ist bestellt
          </button>
        )}
      </footer>
    </article>
  );
}
